import React from "react";
import { ChevronRight, BookOpen } from "lucide-react";
import { PHASE_ORDER } from "../lib/cycle";
import { useI18n } from "../i18n";
import PhaseArticle from "./PhaseArticle";
import InfoArticle from "./InfoArticle";

const EntryCard = React.forwardRef(function EntryCard(
  { color, label, title, readTime, refText, testid, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      data-testid={testid}
      className="flex items-center gap-3 bg-bone border border-border rounded-2xl p-4 cursor-pointer transition-transform hover:-translate-y-0.5 active:scale-[0.99]"
      {...rest}
    >
      <span className="h-11 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
          {label} · {readTime}
        </div>
        <h4 className="font-serif text-lg leading-tight mt-0.5" style={{ color }}>
          {title}
        </h4>
        {refText && <div className="text-[11px] text-muted-foreground mt-0.5">{refText}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  );
});

export default function Library() {
  const { phases, water, protein, t } = useI18n();
  const refText = (art) => {
    const n = (art.references || []).length;
    if (!n) return null;
    return `${n} ${n === 1 ? t("sourceCited") : t("sourcesCited")}`;
  };
  const nutrition = [
    { article: water, color: "#3E7CB1", label: t("hydrationLabel"), key: "water" },
    { article: protein, color: "#9B3926", label: t("nutritionLabel"), key: "protein" },
  ];
  const phaseList = PHASE_ORDER.map((k) => phases[k]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="library-view">
      <div className="bg-bone border border-border rounded-3xl p-6">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold text-primary">
          <BookOpen className="h-3.5 w-3.5" /> {t("libraryKicker")}
        </div>
        <h2 className="font-serif text-3xl mt-2 leading-tight">
          {t("libraryTitle1")}
          <br />
          <span className="text-primary italic">{t("libraryTitle2")}</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t("librarySub")}</p>
      </div>

      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 px-1">
          {t("nutritionWellness")}
        </div>
        <div className="space-y-3">
          {nutrition.map((n) => (
            <InfoArticle key={n.key} article={n.article} color={n.color} label={n.label} testid={`library-article-${n.key}`}>
              <EntryCard color={n.color} label={n.label} title={n.article.title} readTime={n.article.readTime} refText={refText(n.article)} testid={`library-entry-${n.key}`} />
            </InfoArticle>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 px-1">
          {t("yourCyclePhases")}
        </div>
        <div className="space-y-3">
          {phaseList.map((p) => (
            <PhaseArticle key={p.key} phase={p}>
              <EntryCard color={p.color} label={p.label} title={p.article.title} readTime={p.article.readTime} refText={refText(p.article)} testid={`library-entry-${p.key}`} />
            </PhaseArticle>
          ))}
        </div>
      </div>
    </div>
  );
}
